import { type QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { DEFAULT_LOCALE, isLocale, type Locale } from '@/i18n/locales'
import {
  adaptActress,
  adaptSubscription,
  adaptSuggestion,
  adaptTag,
  adaptTagRef,
  adaptVideoCard,
  adaptVideoDetail,
  mapItems,
  usesMockAPI,
} from './api/backend-adapters'
import { api, pageMeta } from './api/client'
import type {
  Actress,
  AdminStats,
  AdminUser,
  CrawlerStatus,
  PageMeta,
  SearchSuggestion,
  Tag,
  TagRef,
  VideoCard,
  VideoDetail,
} from './api/types'
import { useAccountKey } from './session'

export const AUTO_APPEND_CAP = 300

export function useLocale(): Locale {
  const params = useParams({ strict: false }) as { locale?: string }
  return params.locale && isLocale(params.locale) ? params.locale : DEFAULT_LOCALE
}

function useScope() {
  const account = useAccountKey()
  const locale = useLocale()
  return [account, locale] as const
}

type Query = Record<string, string | number | boolean | undefined | null>

export interface Page<T> {
  items: T[]
  meta: PageMeta
}

async function fetchPage<T>(
  path: string,
  query: Query,
  signal: AbortSignal,
  locale = DEFAULT_LOCALE,
): Promise<Page<T>> {
  const { data, meta } = await api<T[]>(path, { query, signal })
  let items = data
  if (!usesMockAPI) {
    if (
      path === '/api/videos' ||
      path === '/api/updates' ||
      /^\/api\/web\/actresses\/[^/]+\/videos$/.test(path)
    ) {
      items = mapItems(data, (item) => adaptVideoCard(item, locale)) as T[]
    } else if (path === '/api/actresses') {
      items = mapItems(data, adaptActress) as T[]
    }
  }
  return { items, meta: pageMeta(meta) }
}

export function useInfiniteList<T extends { id: string }>(
  name: string,
  path: string,
  query: Query,
  startPage = 1,
) {
  const scope = useScope()
  const requestQuery = { ...query }
  let requestPath = path
  if (!usesMockAPI && path === '/api/updates') requestQuery.timeline = true
  if (!usesMockAPI && name === 'actress-videos' && typeof query.actressId === 'string') {
    requestPath = `/api/web/actresses/${encodeURIComponent(query.actressId)}/videos`
    delete requestQuery.actressId
  }
  const result = useInfiniteQuery({
    queryKey: [...scope, name, query, startPage],
    initialPageParam: startPage,
    queryFn: ({ pageParam, signal }) =>
      fetchPage<T>(requestPath, { ...requestQuery, page: pageParam }, signal, scope[1]),
    getNextPageParam: (last) => (last.meta.hasMore ? last.meta.page + 1 : undefined),
    staleTime: 60_000,
  })
  const seen = new Set<string>()
  const items: T[] = []
  for (const page of result.data?.pages ?? [])
    for (const item of page.items) {
      if (seen.has(item.id)) continue
      seen.add(item.id)
      items.push(item)
    }
  const lastPage = result.data?.pages.at(-1)?.meta.page ?? startPage
  return { ...result, items, lastPage, total: result.data?.pages[0]?.meta.total ?? null }
}

// Bounded home shelves reuse the authenticated catalogue and account/locale cache scope.
export function useHomeVideos(favorites = false) {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, favorites ? 'favorites' : 'home-random', 'home'],
    enabled: scope[0] !== 'anon',
    queryFn: ({ signal }) =>
      fetchPage<VideoCard>(
        '/api/videos',
        favorites
          ? { page: 1, limit: 5, sortBy: 'releaseDate', dir: 'desc', favorited: true }
          : { random: true, limit: 10 },
        signal,
        scope[1],
      ),
    staleTime: favorites ? 60_000 : Infinity,
    refetchOnWindowFocus: favorites,
    refetchOnReconnect: favorites,
  })
}

export function useVideoDetail(id: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'video', id],
    queryFn: ({ signal }) =>
      api<VideoDetail>(`/api/videos/${encodeURIComponent(id)}`, { signal, query: { webStatic: true } }).then(
        (r) => (usesMockAPI ? r.data : adaptVideoDetail(r.data, scope[1])),
      ),
  })
}

export function useActress(id: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'actress', id],
    queryFn: ({ signal }) =>
      api<Actress>(`${usesMockAPI ? '/api/actresses' : '/api/web/actresses'}/${encodeURIComponent(id)}`, {
        signal,
      }).then((r) => (usesMockAPI ? r.data : adaptActress(r.data))),
  })
}

export function useSubscriptions() {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'subscriptions'],
    queryFn: async ({ signal }) => {
      if (usesMockAPI)
        return api<(Actress & { newCount: number | null })[]>('/api/subscriptions', { signal }).then(
          (r) => r.data,
        )
      const items: (Actress & { newCount: number | null })[] = []
      for (let page = 1; ; page += 1) {
        const result = await api<unknown>('/api/subscriptions', { signal, query: { page, limit: 100 } })
        items.push(...mapItems(result.data, adaptSubscription))
        if (!pageMeta(result.meta).hasMore) return items
      }
    },
  })
}

export function useTags(q: string) {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'tags', q],
    queryFn: ({ signal }) =>
      api<Tag[]>('/api/tags', { signal, query: { q } }).then((r) =>
        usesMockAPI ? r.data : mapItems(r.data, adaptTag),
      ),
    staleTime: 5 * 60_000,
  })
}

export function useHotTags() {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'tags-hot'],
    queryFn: ({ signal }) =>
      api<Tag[]>(usesMockAPI ? '/api/tags/hot' : '/api/tags', {
        signal,
        query: usesMockAPI ? undefined : { sort: 'favorites', limit: 12 },
      }).then((r) => (usesMockAPI ? r.data : mapItems(r.data, adaptTag))),
    staleTime: 5 * 60_000,
  })
}

export function useTag(id: string | undefined) {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'tag', id],
    enabled: !!id,
    queryFn: ({ signal }) =>
      api<TagRef>(`${usesMockAPI ? '/api/tags' : '/api/web/tags'}/${encodeURIComponent(id ?? '')}`, {
        signal,
      }).then((r) => (usesMockAPI ? r.data : adaptTagRef(r.data))),
  })
}

export function useSuggestions(q: string) {
  const scope = useScope()
  const trimmed = q.trim()
  return useQuery({
    queryKey: [...scope, 'suggest', trimmed],
    enabled: trimmed.length >= 2,
    queryFn: ({ signal }) =>
      api<SearchSuggestion[]>('/api/search/suggestions', {
        signal,
        query: { q: trimmed, scope: 'all' },
      }).then((r) => (usesMockAPI ? r.data : mapItems(r.data, (item) => adaptSuggestion(item, scope[1])))),
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  })
}

export function useAdminStats() {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'admin-stats'],
    queryFn: ({ signal }) => api<AdminStats>('/api/admin/stats', { signal }).then((r) => r.data),
  })
}

export function useAdminUsers(query: Query) {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'admin-users', query],
    queryFn: ({ signal }) => fetchPage<AdminUser>('/api/admin/users', query, signal),
    placeholderData: (prev) => prev,
  })
}

export function useCrawlerStatus() {
  const scope = useScope()
  return useQuery({
    queryKey: [...scope, 'crawler-status'],
    queryFn: ({ signal }) => api<CrawlerStatus>('/api/admin/crawler/status', { signal }).then((r) => r.data),
    refetchInterval: 5000,
  })
}

export function patchEntity(queryClient: QueryClient, id: string, patch: Record<string, unknown>) {
  const visit = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      let changed = false
      const next = value.map((v) => {
        const r = visit(v)
        if (r !== v) changed = true
        return r
      })
      return changed ? next : value
    }
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>
      let changed = false
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(obj)) {
        const r = visit(v)
        if (r !== v) changed = true
        next[k] = r
      }
      if (obj.id === id) {
        for (const [k, v] of Object.entries(patch))
          if (k in obj && obj[k] !== v) {
            next[k] = v
            changed = true
          }
      }
      return changed ? next : value
    }
    return value
  }
  queryClient.setQueriesData({ predicate: (q) => q.queryKey[0] !== 'session' }, (old: unknown) => visit(old))
}

export type { VideoCard }
